import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Api } from '../../../typings/api';
import {
	Wrapper,
	Header,
	ButtonClose,
	DriveListWrapper,
	EmptyFolderLabel,
} from './styles';
import Portal from '../../Portal';
import HeaderLogo from '../../../containers/MenuManager/Header/Logo';
import Toggle from '../../Toggle';
import Breadcrumbs from './Breadcrumbs';
import DriveItemList from '../../DriveItemList';
import { ListDisplayType } from '../../../containers/DriveItemList/constants';
import { getOrFetchDriveItemMeta, setBreadcrumbs, resetBreadcrumbs } from '../../../containers/DriveItemList/actions';
import { DriveListState, FetchStatus, getDriveItemStatusFromMap, DriveItemFetchState } from '../../../containers/DriveItemList/reducer';
import UserAgentUtils from '../../../utils/userAgent';
import useResetDrivePage from '../../../utils/customHooks/useResetDrivePage';

interface FromAppProps {
	driveList: DriveListState;
	currentDriveId: number;
	privateDirId: number;
	sharedDirId: number;
	applicationId: number;
	isValidResource: (item: Api.DriveItem) => boolean;
	onClose: () => void;
	onSelect: (item: Api.DriveItem) => void;
	dispatch: Dispatch<StoreState>;
	actionButtonColor?: string;
}

enum RootDriveLabels {
	my_documents = 'messages.my_documents',
	my_app = 'messages.my_app',
}

interface RootDriveState {
	id: number;
	label: RootDriveLabels;
}

const getDriveItemIds = (
	currentDriveId: number,
	driveList: DriveListState,
	isValidResource: (item: Api.DriveItem) => boolean,
	privateDirId: number,
	sharedDirId: number
): number[] => {
	const { driveItems, driveItemsFetchStatus } = driveList;

	if (currentDriveId) {
		const driveFolder = driveItems[currentDriveId];

		if (driveFolder) {
			const { resources } = driveFolder;
			if (resources) {
				const isPrivateDir = currentDriveId === privateDirId;
				const additionalFolders = sharedDirId && isPrivateDir ? [sharedDirId] : [];
				return [
					...additionalFolders,
					...resources
						.filter((item) => {
							const driveItemFetchStatus = getDriveItemStatusFromMap(driveItemsFetchStatus, item.id);
							const { metaFetchStatus } = driveItemFetchStatus;
							const isPrivateDir = item.id === privateDirId;
							const driveItem = {
								...item,
								name: driveItems[item.id]
									? driveItems[item.id].name
									: undefined,
								componentType: driveItems[item.id]
									? driveItems[item.id].componentType
									: undefined,
							};

							return (driveItem.type === Api.DriveItemType.Directory || isValidResource(driveItem as Api.DriveItem))
								&& metaFetchStatus !== FetchStatus.ERROR
								&& !isPrivateDir;
						})
						.map(({ id }) => id),
				];
			}
		}
	}

	return [];
};

const isFetchingState = (driveFolderId: number | null, driveItemsFetchStatus: { [k: number]: DriveItemFetchState}): boolean => {
	if (driveFolderId && !UserAgentUtils.isMobileApp) {
		const driveFolderFetchStatus = getDriveItemStatusFromMap(driveItemsFetchStatus, driveFolderId);
		const { contentBlocksStatus, metaFetchStatus } = driveFolderFetchStatus;

		if (metaFetchStatus !== FetchStatus.FETCHED && metaFetchStatus !== FetchStatus.ERROR) {
			return true;
		}

		const blocks = Object.keys(contentBlocksStatus);
		for (let i = 0; i < blocks.length; i++) {
			const block = blocks[i];
			const blockStatus: FetchStatus = contentBlocksStatus[block];

			if (blockStatus === FetchStatus.FETCHING) {
				return true;
			}
		}
	}
	return false;
};

const FromApp = ({
	driveList,
	currentDriveId,
	privateDirId,
	sharedDirId,
	applicationId,
	isValidResource,
	onClose,
	onSelect,
	dispatch,
	actionButtonColor,
}: FromAppProps) => {
	const rootDriveConfig: { [k: string]: RootDriveState } = {
		[applicationId]: {
			id: applicationId,
			label: RootDriveLabels.my_app,
		},
		[privateDirId]: {
			id: privateDirId,
			label: RootDriveLabels.my_documents,
		},
	};
	const [rootDriveId, setRootDriveId] = React.useState(applicationId);
	const shouldResetDrivePage = useResetDrivePage([currentDriveId]);

	const getFolder = (id: number, label: string): void => {
		if (id === sharedDirId) {
			label = 'messages.shared_with_me';
		}

		dispatch(setBreadcrumbs({ id, label }));

		if (id && !UserAgentUtils.isMobileApp) {
			dispatch(getOrFetchDriveItemMeta(id));
		}
	};

	const handleRootDriveToggleClick = (id: number): void => {
		setRootDriveId(id);
		dispatch(resetBreadcrumbs());
		getFolder(id, rootDriveConfig[id].label);
	};

	const handleBreadcrumbClick = (id: number, label: string): void => {
		getFolder(id, label);
	};

	const handleDriveItemClick = (item: Api.DriveItem): void => {
		const { type, id, label } = item;
		if (type === Api.DriveItemType.Directory) {
			getFolder(id, label);
		} else {
			onSelect(item);
			onClose();
		}
	};

	React.useEffect(() => {
		getFolder(rootDriveId, rootDriveConfig[rootDriveId].label);
		return () => {
			dispatch(resetBreadcrumbs());
		};
	}, []);

	const { driveItems, driveItemsFetchStatus, breadcrumbs } = driveList;
	const isFetching = isFetchingState(currentDriveId, driveItemsFetchStatus);
	const driveItemIds = getDriveItemIds(currentDriveId, driveList, isValidResource, privateDirId, sharedDirId);
	const isEmptyFolder = !isFetching && driveItemIds.length < 1;

	return (
		<Portal parentNode={document.body}>
			<Wrapper>
				<Header>
					<HeaderLogo isDisabled={true} />
					<ButtonClose
						type="button"
						onClick={onClose}
						color={actionButtonColor}
					>
						<Translate value="messages.cancel" />
					</ButtonClose>
				</Header>
				<Toggle
					items={Object.values(rootDriveConfig)}
					activeId={rootDriveId}
					onItemClick={handleRootDriveToggleClick}
				/>
				<Breadcrumbs
					items={breadcrumbs}
					onItemClick={handleBreadcrumbClick}
				/>
				<DriveListWrapper>
					{isEmptyFolder && <EmptyFolderLabel value="messages.no_assets" />}
					<DriveItemList
						hasBottomBar={false}
						displayType={ListDisplayType.Row}
						storeDriveItems={driveItems}
						driveItemIdsToDisplay={driveItemIds}
						folderId={currentDriveId}
						sharedDirId={undefined}
						isPrivateDir={false}
						isFetching={isFetching}
						showUnreadBadge={false}
						showSearchField={false}
						hasActions={false}
						navigationItems={[]}
						isEditMode={false}
						isEditButtonDisplayed={false}
						isDisplayTypeDropdownDisplayed={false}
						isLoading={false}
						shouldResetPage={shouldResetDrivePage}
						onDriveItemClick={handleDriveItemClick}
						dispatch={dispatch}
					/>
				</DriveListWrapper>
			</Wrapper>
		</Portal>
	);
};

export default FromApp;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ResourceSelector/FromApp/index.tsx